"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.chainApproversQuerySchema = exports.REQUISITION_ACTIONS = exports.updateSlaRuleSchema = exports.escalationRungSchema = exports.ESCALATION_RECIPIENTS = exports.updateApprovalChainSchema = exports.approvalChainSchema = exports.CHAIN_APPLIES_TO = exports.chainStepSchema = exports.chainApproverSchema = exports.requisitionListQuerySchema = exports.decideApprovalSchema = exports.APPROVAL_DECISIONS = exports.assignRecruiterSchema = exports.cancelRequisitionSchema = exports.holdRequisitionSchema = exports.resumeRequisitionSchema = exports.reviseRequisitionSchema = exports.submitRequisitionSchema = exports.BUDGET_RANGE_PROBLEM = exports.START_RANGE_PROBLEM = exports.updateRequisitionSchema = exports.createRequisitionSchema = exports.HIRE_KIND_LABELS = exports.HIRE_KINDS = exports.REQUESTABLE_POSITION_STATUSES = exports.ACTIVE_REQUISITION_STATUSES = exports.routePreviewQuerySchema = exports.HR_CHECK_STEP = exports.STANDARD_APPROVAL_STEPS = exports.REQUISITION_STATUS_LABELS = exports.REQUISITION_PRIORITY_LABELS = exports.requisitionPrioritySchema = exports.SLA_STATE_LABELS = exports.SLA_STATES = exports.SLA_SCOPES = exports.REQUISITION_ENTITY = void 0;
exports.startRangeProblem = startRangeProblem;
exports.budgetRangeProblem = budgetRangeProblem;
exports.missingForSubmit = missingForSubmit;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const money_1 = require("./money");
const schemas_1 = require("./schemas");
const calendars_1 = require("./calendars");
const jev_budget_1 = require("./jev-budget");
const request_content_1 = require("./request-content");
const hiring_templates_1 = require("./hiring-templates");
// S9 — requisitions, approval chains, and the SLA clocks that time them.
//
// Sources: master plan S9; §L25 (the requisition machine, in state-machines.ts);
// F4.01–F4.13 and F16.03–F16.08; E2's request-to-hire fields; §L26 messages.
// Product-owner decisions of 2026-09-28: priority is three levels; a parallel step
// needs every approver; a role step goes to that role's holders for the
// requisition's department, first to act completes it; the board is a table.
exports.REQUISITION_ENTITY = 'requisition';
/** The two clocks S9 runs. Both are working-time clocks (S6), shown elapsed and active. */
exports.SLA_SCOPES = {
    /** One per approval step, from the moment it becomes current. Ball: approver. */
    APPROVAL: 'requisition.approval',
    /** From `requisition.approved` (time-to-fill starts here) to a job existing. Ball: recruiter. */
    APPROVED: 'requisition.approved',
    /**
     * S15: an application's time in a stage, keyed by the stage itself — the key the
     * seeded application rules already use. The limit is the job's own
     * `pipeline_stages.sla_hours`, else the rule's. Ball: recruiter.
     */
    SCREENING: 'screening',
    ASSESSMENT: 'assessment',
    INTERVIEW_1: 'interview_1',
    /** S21: rounds 2 and 3 get the clock round 1 has (ADR 0021's Consequences). */
    INTERVIEW_2: 'interview_2',
    INTERVIEW_3: 'interview_3',
};
exports.SLA_STATES = ['on_track', 'warning', 'breached', 'paused', 'stopped'];
exports.SLA_STATE_LABELS = {
    on_track: 'On track',
    warning: 'Due soon',
    breached: 'Overdue',
    paused: 'Paused',
    stopped: 'Done',
};
exports.requisitionPrioritySchema = zod_1.z.enum(enums_1.REQUISITION_PRIORITIES);
exports.REQUISITION_PRIORITY_LABELS = {
    low: 'Low',
    normal: 'Normal',
    urgent: 'Urgent',
};
exports.REQUISITION_STATUS_LABELS = {
    draft: 'Draft',
    submitted: 'Submitted',
    in_approval: 'In approval',
    approved: 'Approved',
    rejected: 'Rejected',
    converted: 'Job created',
    on_hold: 'On hold',
    filled: 'Filled',
    partially_filled: 'Partly filled',
    cancelled: 'Cancelled',
    reopened: 'Reopened',
};
/**
 * ADR 0073 / workflow review 2026-10-29: every hire request, HR's own included, goes through
 * two layers — an HR check (the "checker"), then Leadership's yes (the "approver"). A role step
 * is decided by whoever acts first.
 *
 * §L41 (owner, DECISIONS-LOG 2026-11-02 Q1 a): the HR check is **always** first, chain or no
 * chain, so HR can edit the request and sees its Jev budget there (ADR 0086 §4). A matching
 * approval chain replaces only the Leadership step.
 */
exports.STANDARD_APPROVAL_STEPS = [
    { stepNumber: 1, name: 'HR check', roleKey: 'hr_lead' },
    { stepNumber: 2, name: 'Leadership approval', roleKey: 'leadership' },
];
exports.HR_CHECK_STEP = exports.STANDARD_APPROVAL_STEPS[0];
/**
 * GET /requisitions/route-preview — who a request sent from this department will go to (§L41). The same plan `route()` uses,
 * so the form never promises a route the server won't take.
 */
exports.routePreviewQuerySchema = zod_1.z.object({ departmentId: schemas_1.uuidSchema });
/** Statuses the one-active-requisition-per-position index counts (S5). */
exports.ACTIVE_REQUISITION_STATUSES = enums_1.REQUISITION_STATUSES.filter((status) => !['rejected', 'cancelled', 'filled', 'partially_filled'].includes(status));
/**
 * Position statuses a request may be raised against. Workflow review 2026-10-29: "Existing
 * position", not "existing vacant position" — a seat is often replaced before it is empty,
 * so a filled seat may be requested too (for a replacement or an additional hire).
 */
exports.REQUESTABLE_POSITION_STATUSES = ['vacant', 'becoming_vacant', 'planned', 'filled'];
/** Workflow review 2026-10-29: on an existing position, is the hire replacing someone or adding a seat? */
exports.HIRE_KINDS = ['replacement', 'additional'];
exports.HIRE_KIND_LABELS = {
    replacement: 'Replacement hiring',
    additional: 'Additional hiring',
};
// --- REQ-01: the request-to-hire form ------------------------------------------
const reasonSchema = zod_1.z
    .string()
    .trim()
    .min(1, 'Write a reason before continuing.')
    .max(2000, 'Keep the reason under 2000 characters.');
/**
 * F4.01 — a draft. Either an existing position, or a new one described by title,
 * department and grade (E2). Everything is optional on a draft; F4.02 decides what
 * submitting needs, so half a request can be saved and finished later.
 */
const draftFields = {
    positionId: schemas_1.uuidSchema.nullable().optional(),
    departmentId: schemas_1.uuidSchema.nullable().optional(),
    title: zod_1.z.string().trim().max(160).nullable().optional(),
    grade: zod_1.z.string().trim().max(32).nullable().optional(),
    slotsRequested: schemas_1.headcountSchema.optional(),
    justification: zod_1.z.string().trim().max(4000).nullable().optional(),
    priority: exports.requisitionPrioritySchema.optional(),
    /** Workflow review 2026-10-29: the start is a range — ideally from this date… */
    targetStartDate: calendars_1.isoDateSchema.nullable().optional(),
    /** …and at the latest by this one. */
    targetStartLatest: calendars_1.isoDateSchema.nullable().optional(),
    /** The budget is a range too: from this amount (minor units)… */
    budgetAmountMinor: money_1.minorUnitsSchema.nullable().optional(),
    /** …up to this one. */
    budgetMaxMinor: money_1.minorUnitsSchema.nullable().optional(),
    budgetCurrency: money_1.currencyCodeSchema.nullable().optional(),
    /** On an existing position: replacing someone, or an additional seat. */
    hireKind: zod_1.z.enum(exports.HIRE_KINDS).nullable().optional(),
    /** For a replacement: who is being replaced, from the staff list (the requester's own team). */
    replacesStaffId: schemas_1.uuidSchema.nullable().optional(),
    hiringManagerUserId: schemas_1.uuidSchema.nullable().optional(),
    /** ADR 0070: how much Jev (millionths of a dollar) the manager says this job may need; null = no ask. */
    jevBudgetMicroUsd: jev_budget_1.jevBudgetMicroUsdSchema.nullable().optional(),
    /** ADR 0075: the role itself, in the requester's words; goes into the job's responsibilities. Required to send. */
    responsibilities: zod_1.z.string().trim().max(20000).nullable().optional(),
    /** ADR 0075: the brief's four parts as points. "Why we need this role" is required to send and is kept as `justification` too. */
    brief: request_content_1.requestBriefSchema.optional(),
    /** ADR 0075: the guided ideal-candidate text (four prompts); HR turns it into rated lines in the job. */
    idealCandidate: request_content_1.requestIdealCandidateSchema.optional(),
    /** ADR 0075: the job's Basics, every field except the recruiter (owner 2026-11-01 Q1 a). */
    jobDetails: request_content_1.requestJobDetailsSchema.optional(),
    /** ADR 0083: the Ideal candidate profile template, the 30 and 90-day plans, and whether the role manages people. All optional to send. */
    idealProfile: hiring_templates_1.idealProfileSchema.optional(),
    plan30: hiring_templates_1.plan30Schema.optional(),
    plan90: hiring_templates_1.plan90Schema.optional(),
    managesPeople: zod_1.z.boolean().optional(),
};
exports.createRequisitionSchema = zod_1.z.object(draftFields);
exports.updateRequisitionSchema = schemas_1.versionedSchema.extend(draftFields);
const REQUIRED = 'This field is required.';
exports.START_RANGE_PROBLEM = "The latest start date can't be before the earliest one.";
/**
 * Evening review 2026-11-01 item 3: a latest start date before the earliest is refused, on every save (draft or send) and on the
 * form as soon as both are picked. Both are `YYYY-MM-DD`, so comparing the text compares the days.
 */
function startRangeProblem(earliest, latest) {
    return earliest && latest && latest < earliest ? exports.START_RANGE_PROBLEM : null;
}
exports.BUDGET_RANGE_PROBLEM = 'The top of the budget must be at least the bottom.';
/**
 * QA D48 (2026-11-01): a budget whose top is below its bottom is refused on every save, a draft's too, with this message beside
 * the budget, never a general error. Either end may be missing; amounts are minor units, as text or bigint.
 */
function budgetRangeProblem(from, to) {
    return from !== null && from !== undefined && to !== null && to !== undefined && BigInt(to) < BigInt(from) ? exports.BUDGET_RANGE_PROBLEM : null;
}
/**
 * F4.02 "required fields complete", as field → §L26 message. Empty means it may be
 * submitted. Shared so the form marks the same fields the server refuses.
 */
function missingForSubmit(draft) {
    const missing = {};
    if (!draft.positionId) {
        if (!draft.departmentId)
            missing.departmentId = REQUIRED;
        if (!draft.title?.trim())
            missing.title = REQUIRED;
        // Workflow review 2026-10-29: grade is gone — team leads don't know it.
    }
    else {
        if (!draft.hireKind)
            missing.hireKind = 'Choose replacement or additional hiring.';
        else if (draft.hireKind === 'replacement' && !draft.replacesStaffId) {
            missing.replacesStaffId = 'Choose who is being replaced.';
        }
    }
    // ADR 0075 (section B Q3 a): "why we need this role" and the responsibilities must be written; the rest is optional.
    if (!draft.justification?.trim())
        missing.justification = REQUIRED;
    if (draft.responsibilities !== undefined && !draft.responsibilities?.trim())
        missing.responsibilities = REQUIRED;
    const shift = draft.jobDetails ? (0, request_content_1.shiftProblem)(draft.jobDetails) : null;
    if (shift)
        missing.shift = shift;
    if (!draft.targetStartDate)
        missing.targetStartDate = REQUIRED;
    const startProblem = startRangeProblem(draft.targetStartDate, draft.targetStartLatest ?? null);
    if (startProblem)
        missing.targetStartLatest = startProblem;
    const hasBudget = draft.budgetAmountMinor !== null || (draft.budgetMaxMinor ?? null) !== null;
    if (hasBudget && !draft.budgetCurrency) {
        missing.budgetCurrency = 'Choose a currency for the budget.';
    }
    const budgetProblem = budgetRangeProblem(draft.budgetAmountMinor, draft.budgetMaxMinor);
    if (budgetProblem)
        missing.budget = budgetProblem;
    return missing;
}
// --- Lifecycle actions (§L25) --------------------------------------------------
exports.submitRequisitionSchema = schemas_1.versionedSchema;
exports.reviseRequisitionSchema = schemas_1.versionedSchema;
exports.resumeRequisitionSchema = schemas_1.versionedSchema;
exports.holdRequisitionSchema = schemas_1.versionedSchema.extend({ reason: reasonSchema });
/** The reason is required unless the owner is withdrawing their own draft (§L25). */
exports.cancelRequisitionSchema = schemas_1.versionedSchema.extend({
    reason: zod_1.z.string().trim().max(2000).optional(),
});
exports.assignRecruiterSchema = schemas_1.versionedSchema.extend({ recruiterUserId: schemas_1.uuidSchema });
// --- REQ-05: an approval decision ----------------------------------------------
exports.APPROVAL_DECISIONS = ['approve', 'reject', 'send_back'];
/** F4.04 reason mandatory; F4.05 comment mandatory. */
exports.decideApprovalSchema = schemas_1.versionedSchema
    .extend({
    decision: zod_1.z.enum(exports.APPROVAL_DECISIONS),
    reason: zod_1.z.string().trim().max(2000).optional(),
    /** ADR 0070: an approver who changes the Jev budget sends the amount they approve; absent means as it stands. */
    jevBudgetMicroUsd: jev_budget_1.jevBudgetMicroUsdSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.decision !== 'approve' && !value.reason) {
        ctx.addIssue({ code: 'custom', message: 'Write a reason before continuing.', path: ['reason'] });
    }
});
// --- REQ-02: the board -----------------------------------------------------------
/** `?status=draft,submitted` — one or more of `values`, comma-separated. */
function commaList(values) {
    return zod_1.z
        .string()
        .transform((value) => value.split(',').filter((part) => part !== ''))
        .pipe(zod_1.z.array(zod_1.z.enum(values)).min(1))
        .optional();
}
exports.requisitionListQuerySchema = schemas_1.listQuerySchema.extend({
    status: commaList(enums_1.REQUISITION_STATUSES),
    priority: commaList(enums_1.REQUISITION_PRIORITIES),
    slaState: commaList(exports.SLA_STATES),
    ventureId: schemas_1.uuidSchema.optional(),
    departmentId: schemas_1.uuidSchema.optional(),
});
// --- ADMIN-06: approval chains ---------------------------------------------------
/** A role or a named person — exactly one (master plan S9). */
exports.chainApproverSchema = zod_1.z
    .object({
    approverUserId: schemas_1.uuidSchema.nullable().optional(),
    approverRoleId: schemas_1.uuidSchema.nullable().optional(),
})
    .refine((value) => Boolean(value.approverUserId) !== Boolean(value.approverRoleId), {
    message: 'Choose one person or one role.',
});
/**
 * One step. Several approvers make it parallel: every one must approve, and one
 * rejection ends it (product owner, 2026-09-28). One approver makes it sequential.
 */
exports.chainStepSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name this step.').max(80),
    approvers: zod_1.z.array(exports.chainApproverSchema).min(1, 'Add at least one approver.').max(10),
});
/** What a chain approves (S23, DECISIONS-LOG 2026-09-29 Q5): requisitions, or offers. */
exports.CHAIN_APPLIES_TO = ['requisition', 'offer'];
// Workflow review 2026-10-29 and the Admin redesign (2026-11-02): grade is gone. A chain is chosen by department only, and the
// form no longer sends one (an old client's `grade` is dropped by the schema; an old chain keeps its stored grade, unread).
const approvalChainFields = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Name this chain.').max(120),
    ventureId: schemas_1.uuidSchema,
    departmentId: schemas_1.uuidSchema.nullable().optional(),
    isActive: zod_1.z.boolean().default(true),
    appliesTo: zod_1.z.enum(exports.CHAIN_APPLIES_TO).default('requisition'),
    /**
     * S23 Q3: an offer chain names who approves one more step when an offer is outside the
     * band — a person or a role. Requisition chains have none.
     */
    extraApprover: exports.chainApproverSchema.nullable().default(null),
    steps: zod_1.z.array(exports.chainStepSchema).min(1, 'Add at least one step.').max(10),
});
function checkExtraApprover(input, ctx) {
    if (input.appliesTo === 'offer' && !input.extraApprover) {
        ctx.addIssue({ code: 'custom', path: ['extraApprover'], message: 'Choose who approves an offer outside the pay band.' });
    }
    if (input.appliesTo === 'requisition' && input.extraApprover) {
        ctx.addIssue({ code: 'custom', path: ['extraApprover'], message: 'Only offer chains have an extra approver.' });
    }
}
exports.approvalChainSchema = approvalChainFields.superRefine(checkExtraApprover);
exports.updateApprovalChainSchema = approvalChainFields.extend(schemas_1.versionedSchema.shape).superRefine(checkExtraApprover);
// --- F16.08: SLA rules -------------------------------------------------------------
exports.ESCALATION_RECIPIENTS = ['manager', 'hr_lead'];
exports.escalationRungSchema = zod_1.z.object({
    /** Past 100%: 150 means at one and a half times the limit. */
    atPercent: zod_1.z.number().int().min(101, 'An escalation comes after the breach.').max(1000),
    recipient: zod_1.z.enum(exports.ESCALATION_RECIPIENTS),
});
exports.updateSlaRuleSchema = schemas_1.versionedSchema.extend({
    targetWorkingHours: zod_1.z.number().int().min(1, 'At least one working hour.').max(2000),
    /** Master plan S9: warning at 80%. Kept editable, as the table always allowed. */
    warningAtPercent: zod_1.z.number().int().min(50).max(99).default(80),
    escalationChain: zod_1.z.array(exports.escalationRungSchema).max(5).default([]),
});
exports.REQUISITION_ACTIONS = [
    'edit',
    'submit',
    'revise',
    'hold',
    'resume',
    'cancel',
    'assign_recruiter',
];
/** GET /admin/approval-chains/approvers?appliesTo=… — only the roles and people who can decide such a step (no cap; the page searches). */
exports.chainApproversQuerySchema = zod_1.z.object({ appliesTo: zod_1.z.enum(exports.CHAIN_APPLIES_TO).default('requisition') });
//# sourceMappingURL=requisitions.js.map